import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: window.sessionStorage
  },
  global: {
    headers: {
      'X-Client-Info': 'pokemon-student-cards@1.0.0'
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Helper function to check if a Supabase error is a "not found" error
export function isNotFoundError(error: any) {
  return error?.code === 'PGRST116' || 
         error?.message?.includes('JSON object requested, multiple (or no) rows returned') ||
         error?.message?.includes('The result contains 0 rows');
}

// Helper function to check Supabase connection
export async function checkSupabaseConnection() {
  try {
    const { error } = await supabase.from('cards').select('id').limit(1);
    return !error;
  } catch (error) {
    console.error('Supabase connection error:', error);
    return false;
  }
}

// Helper function to safely fetch a single row with default values
export async function fetchSingleRow<T>(
  query: Promise<{ data: T | null; error: any }>,
  defaultValue: T
): Promise<{ data: T; error: any | null }> {
  try {
    const { data, error } = await query;
    
    // If we get a "not found" error or no data, return the default value
    if (isNotFoundError(error) || !data) {
      return { data: defaultValue, error: null };
    }
    
    // If we have any other error, return it
    if (error) {
      return { data: defaultValue, error };
    }

    // Otherwise return the data
    return { data, error: null };
  } catch (error) {
    return { data: defaultValue, error };
  }
}

// Helper function to ensure combat stats exist for a user
export async function ensureCombatStats(userId: string) {
  try {
    const { error } = await supabase.rpc('ensure_combat_stats', {
      user_id: userId
    });
    
    if (error) {
      console.error('Error ensuring combat stats:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error ensuring combat stats:', error);
    return false;
  }
}